/**
 * ElectricalSensorModalClassic — Monolithic electrical sensor modal template.
 *
 * Read-only electrical sensor display panel:
 * - Power: voltage, current, watt in a 3-card grid
 * - Energy: total kWh consumption
 * - Live: real-time watt display (large number)
 *
 * No commands — purely observational.
 */
import { useCallback, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import './ElectricalSensorModalClassic.css'

// ---------------------------------------------------------------------------
// Section definitions (exported for registry)
// ---------------------------------------------------------------------------

export const ELECTRICAL_SENSOR_CLASSIC_SECTIONS: ModalSectionMeta[] = [
  // ── Modal Background (header + overall) ──
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [
    { id: 'header-back-icon', label: 'Back Arrow Icon', editableProps: ['color', 'fontSize'] },
    { id: 'header-title', label: 'Device Name Text', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'header-edit-icon', label: 'Edit Icon', editableProps: ['color', 'fontSize'] },
  ]},
  // ── Power Section ──
  { id: 'power', label: 'Power Metrics', icon: 'bolt', slots: [
    { id: 'power-label', label: 'Title "POWER METRICS"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'power-card', label: 'Metric Card Container', editableProps: ['background', 'borderRadius', 'padding', 'border'] },
    { id: 'power-card-icon', label: 'Metric Card Icon', editableProps: ['fontSize', 'color'] },
    { id: 'power-card-name', label: 'Metric Card Label', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'power-card-value', label: 'Metric Card Value', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'power-card-unit', label: 'Metric Card Unit', editableProps: ['fontSize', 'color'] },
  ]},
  // ── Energy Section ──
  { id: 'energy', label: 'Energy Consumption', icon: 'electric_meter', slots: [
    { id: 'energy-label', label: 'Title "ENERGY"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'energy-card', label: 'Energy Card Container', editableProps: ['background', 'borderRadius', 'padding', 'border'] },
    { id: 'energy-value', label: 'kWh Value', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'energy-unit', label: 'Unit "kWh"', editableProps: ['fontSize', 'color'] },
    { id: 'energy-sub', label: 'Sub-label Text', editableProps: ['fontSize', 'color'] },
  ]},
  // ── Live Section ──
  { id: 'live', label: 'Live Power', icon: 'flash_on', slots: [
    { id: 'live-label', label: 'Title "LIVE POWER"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'live-value', label: 'Live Watt Number', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'live-unit', label: 'Unit "W"', editableProps: ['fontSize', 'color'] },
    { id: 'live-pf', label: 'Power Factor Text', editableProps: ['fontSize', 'color'] },
  ]},
]

const DEFAULT_SECTION_ORDER = ELECTRICAL_SENSOR_CLASSIC_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}

function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  const v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ElectricalSensorModalClassic({
  device,
  deviceState,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
}: DeviceModalTemplateProps) {
  // ---- Context menu for editor mode ----
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  // ---- Section ordering ----
  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- State from device ----
  const voltage = numAttr(deviceState, 'voltage')
  const current = numAttr(deviceState, 'current')
  const watt = numAttr(deviceState, 'watt', 'power')
  const kwh = numAttr(deviceState, 'kwh', 'energy')
  const powerFactor = numAttr(deviceState, 'powerFactor')

  // Editor-mode fallback values
  const displayVoltage = voltage ?? (isEditorMode ? 220.4 : null)
  const displayCurrent = current ?? (isEditorMode ? 3.12 : null)
  const displayWatt = watt ?? (isEditorMode ? 686 : null)
  const displayKwh = kwh ?? (isEditorMode ? 142.5 : null)
  const displayPf = powerFactor ?? (isEditorMode ? 0.98 : null)

  const powerCards = useMemo(() => [
    { key: 'voltage', label: 'Voltage', icon: 'electrical_services', unit: 'V', value: displayVoltage, decimals: 1 },
    { key: 'current', label: 'Current', icon: 'settings_input_component', unit: 'A', value: displayCurrent, decimals: 2 },
    { key: 'watt', label: 'Power', icon: 'bolt', unit: 'W', value: displayWatt, decimals: 0 },
  ], [displayVoltage, displayCurrent, displayWatt])

  return (
    <div
      className={`electrical-sensor-modal-classic${isEditorMode ? ' editor-mode' : ''}`}
      data-modal-template-id="electrical-sensor-classic"
      onContextMenuCapture={handleContextMenu}
    >
      {cssOverrides && (
        <style dangerouslySetInnerHTML={{ __html: cssOverrides }} />
      )}

      {/* Header (always first, not reorderable) */}
      <div className="electrical-sensor-modal-classic__header">
        <button className="electrical-sensor-modal-classic__back" onClick={onClose} aria-label="Back">
          <span className="material-symbols-rounded" data-modal-slot="header-back-icon">arrow_back</span>
        </button>
        <h2 className="electrical-sensor-modal-classic__title" data-modal-slot="header-title">{device.name || 'Electrical Sensor'}</h2>
        <button
          className="electrical-sensor-modal-classic__edit"
          onClick={() => {/* future: edit nickname */}}
          aria-label="Edit"
        >
          <span className="material-symbols-rounded" data-modal-slot="header-edit-icon">edit</span>
        </button>
      </div>

      {/* Sections rendered in order */}
      {orderedSections.map((sectionId) => {
        if (!isSectionVisible(sectionId)) return null
        const hl = highlightedSection === sectionId ? ' section-highlighted' : ''
        switch (sectionId) {
          case 'power':
            return (
              <div key="power" className={`electrical-sensor-modal-classic__power${hl}`} data-modal-section="power">
                <div className="electrical-sensor-modal-classic__section-label" data-modal-slot="power-label">POWER METRICS</div>
                <div className="electrical-sensor-modal-classic__power-grid">
                  {powerCards.map((card) => (
                    <div key={card.key} className="electrical-sensor-modal-classic__power-card" data-modal-slot="power-card">
                      <span className="material-symbols-rounded electrical-sensor-modal-classic__power-card-icon" data-modal-slot="power-card-icon">
                        {card.icon}
                      </span>
                      <div className="electrical-sensor-modal-classic__power-card-body">
                        <span className="electrical-sensor-modal-classic__power-card-name" data-modal-slot="power-card-name">
                          {card.label}
                        </span>
                        <div className="electrical-sensor-modal-classic__power-card-reading">
                          <span className="electrical-sensor-modal-classic__power-card-value" data-modal-slot="power-card-value">
                            {card.value !== null ? card.value.toFixed(card.decimals) : '—'}
                          </span>
                          <span className="electrical-sensor-modal-classic__power-card-unit" data-modal-slot="power-card-unit">
                            {card.unit}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          case 'energy':
            return (
              <div key="energy" className={`electrical-sensor-modal-classic__energy${hl}`} data-modal-section="energy">
                <div className="electrical-sensor-modal-classic__section-label" data-modal-slot="energy-label">ENERGY</div>
                <div className="electrical-sensor-modal-classic__energy-card" data-modal-slot="energy-card">
                  <span className="material-symbols-rounded electrical-sensor-modal-classic__energy-icon">electric_meter</span>
                  <div className="electrical-sensor-modal-classic__energy-reading">
                    <div className="electrical-sensor-modal-classic__energy-number">
                      <span className="electrical-sensor-modal-classic__energy-value" data-modal-slot="energy-value">
                        {displayKwh !== null ? displayKwh.toFixed(1) : '—'}
                      </span>
                      <span className="electrical-sensor-modal-classic__energy-unit" data-modal-slot="energy-unit">kWh</span>
                    </div>
                    <span className="electrical-sensor-modal-classic__energy-sub" data-modal-slot="energy-sub">Total consumption</span>
                  </div>
                </div>
              </div>
            )
          case 'live':
            return (
              <div key="live" className={`electrical-sensor-modal-classic__live${hl}`} data-modal-section="live">
                <div className="electrical-sensor-modal-classic__section-label" data-modal-slot="live-label">LIVE POWER</div>
                <div className="electrical-sensor-modal-classic__live-display">
                  <div className="electrical-sensor-modal-classic__live-number">
                    <span className="electrical-sensor-modal-classic__live-value" data-modal-slot="live-value">
                      {displayWatt !== null ? displayWatt.toFixed(0) : '—'}
                    </span>
                    <span className="electrical-sensor-modal-classic__live-unit" data-modal-slot="live-unit">W</span>
                  </div>
                  {displayPf !== null && (
                    <span className="electrical-sensor-modal-classic__live-pf" data-modal-slot="live-pf">
                      PF {displayPf.toFixed(2)}
                    </span>
                  )}
                </div>
              </div>
            )
          default:
            return null
        }
      })}
    </div>
  )
}
